import React from "react";
import { Typography, Button, CircularProgress } from "@material-ui/core";
import ReportProblemOutlinedIcon from "@material-ui/icons/ReportProblemOutlined";
import { i18n } from "../../translate/i18n";

// Boundary de MÓDULO — la pieza que hace cumplir la premisa:
//
//   TurnosIA es un módulo de WAIA. Si TurnosIA falla, tiene que fallar SOLO
//   TurnosIA.
//
// React desmonta el árbol ENTERO cuando un componente tira en render. Sin un
// boundary arriba del módulo, un `.map()` sobre algo que no es un array en una
// pantalla de TurnosIA deja a WAIA en blanco: sin chats, sin contactos, sin
// configuración. Eso es exactamente lo que pasó el 24-sep con la v2 de precios.
//
// ⚠ Un boundary NO reemplaza arreglar el dato. Lo que hace es acotar el radio
// de explosión mientras el bug se busca — y cubrir el bug de dentro de dos
// semanas, que todavía no existe.

// Los textos del fallback NO pueden depender de que i18n esté sano: si lo que
// se rompió es justamente el i18n, un `i18n.t` que tire acá deja al boundary
// tirando dentro del boundary, que en React es lo mismo que no tener ninguno.
// Por eso cada clave viene con su texto en duro.
const safeT = (key, fallback) => {
  try {
    const out = i18n.t(key);
    // i18next devuelve la propia clave cuando no resuelve.
    return !out || out === key ? fallback : out;
  } catch {
    return fallback;
  }
};

const VARIANTS = ["page", "inline", "silent", "app"];

class ModuleErrorBoundary extends React.Component {
  state = { error: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    const moduleName = this.props.module || "desconocido";
    // Con contexto suficiente para encontrarlo: qué módulo, en qué ruta, y el
    // componentStack (los nombres NO están minificados en el stack de React).
    // eslint-disable-next-line no-console
    console.error(
      `[ModuleErrorBoundary] ${moduleName} — ${error?.name || "Error"}: ${error?.message || error}`,
      {
        module: moduleName,
        path: typeof window !== "undefined" ? window.location?.pathname : null,
        componentStack: info?.componentStack,
        error
      }
    );
  }

  retry = () => this.setState({ error: null });

  reload = () => window.location.reload();

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const variant = VARIANTS.includes(this.props.variant) ? this.props.variant : "page";

    // La entrada del menú que se cae desaparece; el resto del menú sigue.
    // Mostrar un cartel de error dentro de un <List> sería peor que no mostrar
    // nada: el usuario no puede hacer nada con él y está en TODAS las pantallas.
    if (variant === "silent") return null;

    if (variant === "inline") {
      return (
        <Typography variant="caption" color="textSecondary">
          {safeT("errorBoundary.inline", "No se pudo mostrar esta parte.")}
        </Typography>
      );
    }

    const isApp = variant === "app";

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 12,
          padding: 32,
          minHeight: isApp ? "100vh" : "50vh",
          textAlign: "center"
        }}
      >
        <ReportProblemOutlinedIcon style={{ fontSize: 56, color: "#9e9e9e" }} />
        <Typography variant="h6">
          {isApp
            ? safeT("errorBoundary.appTitle", "Algo falló")
            : safeT("errorBoundary.moduleTitle", "Esta sección no está disponible en este momento")}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {isApp
            ? safeT("errorBoundary.appHelp", "Recargá la página para continuar.")
            : safeT(
                "errorBoundary.moduleHelp",
                "El resto de WAIA sigue funcionando con normalidad. Si vuelve a pasar, avisale al soporte."
              )}
        </Typography>
        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          {!isApp && (
            <Button variant="outlined" size="small" onClick={this.retry}>
              {safeT("errorBoundary.retry", "Reintentar")}
            </Button>
          )}
          <Button variant="contained" color="primary" size="small" onClick={this.reload}>
            {safeT("errorBoundary.reload", "Recargar la página")}
          </Button>
        </div>
      </div>
    );
  }
}

// Fallback del `Suspense` del módulo diferido. Vive acá porque va siempre de a
// pares con el boundary: un chunk que no baja también tiene que quedar contenido.
export const ModuleLoading = () => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "60vh"
    }}
  >
    <CircularProgress />
  </div>
);

export default ModuleErrorBoundary;
